// Severity story (lyse/v1 @ 8.0.0 — audit decisions 1.5 / API-08 / UX Q6).
//
// `Defect.severity` (and `DefectLinkListItem.siblingSeverity`) are NULLABLE
// on the wire as of 8.0.0: `null` = "not graded" — the None-class-model
// population is classified-but-ungraded BY DESIGN, and the gateway no longer
// fabricates `'medium'` for it. The derived 5-string enum survives only as a
// sort/filter convenience; the CANONICAL grade surface is `classCategory` +
// `classColor` + the class-model context (UX Q6: the class-model's own grade
// labels are canonical everywhere — what the DefectDrawer already shows).
//
// This module is the single home for that vocabulary on the FE:
//   * NOT_GRADED_LABEL — the one user-facing label for `null`.
//   * gradeToSeverity  — EXACT mirror of the gateway's
//     `classCategoryToSeverity` (lyse-analysis.service.ts): relative mapping
//     for 3-level models (1→low, 2→medium, 3→high), absolute 1→info …
//     5→critical otherwise, null → null. Used by the WRITE sites that must
//     submit the (unchanged, non-nullable) request-side enum after the UI
//     switched to grade vocabulary.
//   * formatSeverity   — display formatting with the "not graded" branch.
//   * defectSeveritySortRank — sort key for severity columns; `null` = not
//     graded and the CALLER must place it last (ungraded at the bottom)
//     regardless of sort direction.
import type { Defect, DefectSeverity } from "@aeriosiq/types";

/** UX Q6 — ungraded defects display as "Not graded", never a fake grade. */
export const NOT_GRADED_LABEL = "Not graded";

/**
 * Mirror of the BE's `classCategoryToSeverity` (8.0.0). `classCount` is the
 * resolved class model's `class_count`; a 3-level model maps RELATIVE
 * (its worst grade is HIGH), every other (or unknown) model maps absolute.
 */
export function gradeToSeverity(
  classCategory: number | null | undefined,
  classCount?: number | null,
): DefectSeverity | null {
  if (classCategory == null) return null;
  if (classCount === 3) {
    if (classCategory <= 1) return "low";
    if (classCategory === 2) return "medium";
    return "high";
  }
  if (classCategory <= 1) return "info";
  if (classCategory === 2) return "low";
  if (classCategory === 3) return "medium";
  if (classCategory === 4) return "high";
  return "critical";
}

/**
 * Inline display of a (nullable) derived severity. Non-null values render
 * verbatim (the surrounding copy is lowercase wire vocabulary); null renders
 * the canonical "Not graded".
 */
export function formatSeverity(
  severity: DefectSeverity | null | undefined,
): string {
  return severity ?? NOT_GRADED_LABEL;
}

/** Enum rank for legacy rows that carry `severity` but no `classCategory`. */
const SEVERITY_ENUM_RANK: Readonly<Record<DefectSeverity, number>> = {
  info: 1,
  low: 2,
  medium: 3,
  high: 4,
  critical: 5,
};

/**
 * Sort rank for a defect's grade. Prefers the canonical `classCategory`
 * (grade integer); falls back to the derived enum's position for legacy
 * fixtures that predate the column. `null` = not graded — callers sort it
 * LAST regardless of direction (UX Q6 / severity story).
 */
export function defectSeveritySortRank(
  defect: Pick<Defect, "severity"> & { classCategory?: number | null },
): number | null {
  if (defect.classCategory != null) return defect.classCategory;
  if (defect.severity != null) return SEVERITY_ENUM_RANK[defect.severity];
  return null;
}
